import { pendingListingsQuery } from "@/api/listings";
import { queryClient } from "@/api/utils";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Badge } from "@/components/ui/Badge";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Select } from "@givenwell/components";
import { createGlobalState, useGlobalState } from "@givenwell/hooks";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { fallback, number, object, optional, pipe, string, toMinValue } from "valibot";
import { DataTableFooter } from "../../../components/data-table/DataTableFooter";

export const Route = createFileRoute("/admin/listing-review/")({
  component: ListingReviewOverviewPage,
  validateSearch: object({
    q: fallback(optional(string(), ""), ""),
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 100), toMinValue(10)), 100),
  }),
  loader: () => {
    queryClient.prefetchQuery(pendingListingsQuery(undefined));
  },
  head: () => ({ meta: seo({ title: "Listing Review" }) }),
});

const supplierIdState = createGlobalState<string | undefined>(undefined);

function ListingReviewOverviewPage() {
  const { page, "page-size": pageSize } = Route.useSearch();
  const [supplierId, setSupplierId] = useGlobalState(supplierIdState);

  const pendingListings = useQuery(pendingListingsQuery(supplierId, page, pageSize));

  function handleSupplierIdChanged(e: React.ChangeEvent<HTMLSelectElement>) {
    setSupplierId(e.target.value);
  }

  return (
    <PageWrapper title="Review Listings">
      <DataTable>
        <DataTableHeader hideToggle hideSearch>
          <Select size="sm" value={supplierId || ""} onChange={handleSupplierIdChanged} css={{ width: 240 }}>
            <option value="">All suppliers</option>
            {pendingListings.data?.available_suppliers.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </DataTableHeader>
        <DataTableBody
          data={pendingListings.data?.items ?? []}
          status={pendingListings.status}
          columns={[
            {
              header: "Title",
              cell: item => (
                <StyledLink
                  to="/admin/listing-review/$listingId/$version"
                  params={{
                    listingId: item.id,
                    version: item.version,
                  }}
                >
                  {item.title}

                  {item.is_eap && (
                    <>
                      {" "}
                      <Badge color="green">EAP</Badge>
                    </>
                  )}
                </StyledLink>
              ),
            },
            {
              header: "Supplier",
              cell: item => (
                <StyledLink to="/admin/suppliers/$supplierId" params={{ supplierId: item.supplier_id }}>
                  {item.supplier_name}
                </StyledLink>
              ),
            },
            {
              header: "Updated By",
              cell: item => item.updated_by?.name,
            },
            {
              header: "Account Owner",
              cell: item => (item.account_owner ? item.account_owner : null),
            },
            {
              cell: "version",
              header: "Version",
            },
          ]}
          dataKey="id"
        />
        <DataTableFooter itemCount={pendingListings.data?.total_items} />
      </DataTable>
    </PageWrapper>
  );
}
